import type { LeadField, LeadState, TrafficChannelShort } from './crm.types'

export interface CrmLeadFunnelLeadShort {
  id: number
  name: string
  phone: string
  email: string | null
}

export interface CrmLeadFunnelShort {
  id: number
  name: string
  manager?: CrmLeadFunnelManagerShort | null
  manager_id?: number | null
  next_contact_at?: string | null
  state?: LeadState | string
  state_label?: string | null
}

export interface CrmLeadFunnelStatusShort {
  id: number
  name: string
  color: string
}

/** Менеджер в списке воронок (`fio`) и в карточке по воронке (`name`). */
export interface CrmLeadFunnelManagerShort {
  id: number | null
  fio?: string | null
  name?: string
}

/** Ссылка на профиль ВК по id (legacy get-lead → `lead.vk.vk_links`). */
export interface CrmLeadVkLinks {
  by_id: string | null
}

export type CrmChatProviderKey = 'vkontakte' | 'telegram' | 'max' | 'wazzup'

/** Диалог провайдера у лида (get-lead → integrations.{provider}.accounts[].dialogs). */
export type CrmLeadProviderChatDialog = {
  conversation_id: number
  recipient: string
  title: string | null
  unread_count: number
  last_interaction_at: number | null
  attached: true
  external_chat_type: string | null
  external_profile_url: string | null
}

/** Канал Wazzup (account → channels → dialogs). */
export type CrmLeadProviderChatChannel = {
  external_channel_id: string
  channel_id: number | null
  type: string
  name: string | null
  status: string | null
  unread_count: number
  dialogs: CrmLeadProviderChatDialog[]
}

/** Аккаунт интеграции у лида (VK/TG/MAX: dialogs; Wazzup: channels). */
export type CrmLeadProviderChatAccount = {
  integration_id: number
  name: string | null
  status: string | null
  unread_count: number
  dialogs?: CrmLeadProviderChatDialog[]
  channels?: CrmLeadProviderChatChannel[]
}

/**
 * Плоская вкладка диалога для UI чата / дока.
 * Собирается из accounts → (channels →) dialogs.
 */
export type CrmLeadIntegrationChatTab = {
  id: number
  integration_id: number
  conversation_id: number
  provider: string
  name: string | null
  unread_count: number
  last_interaction_at: number | null
  recipient: string
  attached: true
  title?: string | null
  external_channel_id?: string | null
  external_chat_type?: string | null
  external_profile_url?: string | null
}

export type CrmLeadTelephonyEndpoint = {
  id: number
  name: string
  external_id: string | null
  unread_count?: number
  last_interaction_at?: number | null
}

/** Мета чатов провайдера у лида */
export type CrmLeadProviderChatMeta = {
  has_chat: boolean
  unread_count: number
  last_message_text: string | null
  updated_at: string | null
  accounts: CrmLeadProviderChatAccount[]
  /**
   * @deprecated бэк отдаёт `accounts`. Оставляем для старых ответов / локального кэша.
   */
  integrations?: CrmLeadIntegrationChatTab[]
  /** Legacy / optional: профиль ВК */
  vk_user_id?: string | null
  vk_links?: CrmLeadVkLinks | null
  telephony_integrations?: CrmLeadTelephonyEndpoint[]
}

/** get-lead / get-leads / kanban: map по провайдеру */
export type CrmLeadIntegrationsMap = Partial<
  Record<CrmChatProviderKey, CrmLeadProviderChatMeta>
> & {
  telephony?: { endpoints: CrmLeadTelephonyEndpoint[] }
}

/**
 * @deprecated Используйте `integrations.vkontakte` (`CrmLeadProviderChatMeta`).
 * Alias оставлен для постепенной миграции импортов.
 */
export type CrmLeadVkInfo = CrmLeadProviderChatMeta

/** Пустая мета ВК / провайдера (нет диалога). */
export const CRM_LEAD_PROVIDER_CHAT_META_EMPTY: CrmLeadProviderChatMeta = {
  has_chat: false,
  unread_count: 0,
  last_message_text: null,
  updated_at: null,
  accounts: [],
}

/** @deprecated use CRM_LEAD_PROVIDER_CHAT_META_EMPTY */
export const CRM_LEAD_VK_META_EMPTY = CRM_LEAD_PROVIDER_CHAT_META_EMPTY

/**
 * Одна строка таблицы CRM — заявка лида с воронкой.
 * GET `crm-lead/get-leads` → `data[]`.
 */
export interface CrmLeadFunnelListItem {
  /** ID лида в CRM */
  id: number
  /** ID заявки (лид + воронка), ключ строки таблицы */
  lead_funnel_id: number
  funnels: CrmLeadFunnelShort[]
  fields: LeadField[]
  traffic_channel: TrafficChannelShort | null
  user_id: number | null
  user: unknown | null
  is_duplicate: boolean
  last_duplicated_lead: CrmLeadFunnelLeadShort | null
  created_at: string
  entered_at?: string
  /**
   * @deprecated бэк убрал топ-уровень `vk` в пользу `integrations.vkontakte`.
   * Оставляем optional для старых ответов / локальных патчей на переходный период.
   */
  vk?: CrmLeadVkInfo
  /** Мульти-чат CRM-интеграций (vkontakte / telegram / max / wazzup) */
  integrations?: CrmLeadIntegrationsMap
  /** Вложенный лид (если бэк дублирует вместе с `id`) */
  lead?: CrmLeadFunnelLeadShort
  funnel?: CrmLeadFunnelShort | null
  current_status?: CrmLeadFunnelStatusShort | null
  manager?: CrmLeadFunnelManagerShort | null
  next_contact_date?: string | null
  is_primary?: boolean
  type?: LeadState
}

export interface CrmLeadFunnelsListMeta {
  applicationsTotal: number
  leadsTotal: number
  pageCount: number
  currentPage: number
  perPage: number
  applied_filters?: string[] | Record<string, unknown>
  search_type: string | null
}

/** Тело ответа GET `crm-lead/get-leads` (корень: `data[]`, `_meta`, `status`, `user-role`). */
export interface CrmLeadFunnelsListResponse {
  data: CrmLeadFunnelListItem[]
  _meta: CrmLeadFunnelsListMeta
  status: LeadState
  'user-role': string
}

export type ResponseCrmLeadFunnelsList = CrmLeadFunnelsListResponse
